import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import {
  Home,
  Wrench,
  Zap,
  Paintbrush,
  Trees,
  Shield,
  Users,
  Clock,
  Award,
  CheckCircle2,
  ArrowRight,
  Building2,
  Lightbulb,
  TrendingUp,
  Star,
  Sparkles,
  PhoneCall,
  PlayCircle,
} from 'lucide-react';
import apiService from '@/services/api';

// Stock property/services images (Unsplash CDN — stable, no API key required)
const IMG = {
  heroHouse:
    'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=80',
  heroInterior:
    'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=1200&q=80',
  modernHome:
    'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&w=1200&q=80',
  apartmentBuilding:
    'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=1200&q=80',
  livingRoom:
    'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=1200&q=80',
  garden:
    'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?auto=format&fit=crop&w=1200&q=80',
  kitchen:
    'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=1200&q=80',
};

// Hardcoded fallback data for stats
const FALLBACK_STATS = [
  { value: '5,000+', label: 'Professionals' },
  { value: '98%', label: 'Satisfaction' },
  { value: '250+', label: 'Service Types' },
  { value: '< 24hr', label: 'Response Time' }
];

// Hardcoded fallback data for featured services with thematic images
const FALLBACK_SERVICES = [
  { icon: Home, title: 'Home Repairs', description: 'From fixing creaky doors to fixing leaky roofs, we get your entire property running optimally.', image: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=600&q=80' },
  { icon: Zap, title: 'Electrical', description: 'Safety certified professionals for all electrical work from simple upgrades to full rewires.', image: 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?auto=format&fit=crop&w=600&q=80' },
  { icon: Trees, title: 'Landscaping', description: 'Expert outdoor design and maintenance. Green spaces that enhance property value and usability.', image: 'https://images.unsplash.com/photo-1558904541-efa843a96f01?auto=format&fit=crop&w=600&q=80' },
  { icon: Wrench, title: 'Plumbing', description: 'From routine maintenance to emergency repairs. Professional plumbing solutions for all property types.', image: 'https://images.unsplash.com/photo-1585704032915-c3400ca199e7?auto=format&fit=crop&w=600&q=80' },
  { icon: Paintbrush, title: 'Decorating', description: 'Interior and exterior painting, wallpapering, and finishing touches that transform spaces.', image: 'https://images.unsplash.com/photo-1562259949-e8e7689d7828?auto=format&fit=crop&w=600&q=80' },
  { icon: Building2, title: 'Construction', description: 'Structural work, renovations, and custom builds. Complex projects managed end-to-end.', image: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=600&q=80' }
];

// Hardcoded fallback data for testimonials
const FALLBACK_TESTIMONIALS = [
  { name: 'Sarah Mitchell', role: 'Portfolio Manager', company: 'Sterling Properties', quote: 'Virewire has transformed how we manage our 50-property portfolio. Response times have never been better.' },
  { name: 'James Rodriguez', role: 'Property Director', company: 'Luxury Estates Co', quote: 'The quality of professionals on the platform is exceptional. Our tenants are happier, our costs are down.' },
  { name: 'Emma Thompson', role: 'Development Lead', company: 'Urban Housing UK', quote: 'Coordinating contractors across multiple sites used to be chaotic. Now it\'s seamless with Virewire.' }
];

// Hardcoded fallback data for features
const FALLBACK_FEATURES = [
  { icon: Clock, title: 'Smart Scheduling', description: 'Coordination across multiple time zones' },
  { icon: Award, title: 'Quality Control', description: 'Verified professionals every single time' },
  { icon: TrendingUp, title: 'Price Transparency', description: 'Fixed pricing, no surprise costs' },
  { icon: Lightbulb, title: 'Intelligent Matching', description: 'AI-powered service recommendations' },
  { icon: Shield, title: 'Insurance Coverage', description: 'Every job fully insured and protected' },
  { icon: Users, title: 'Account Management', description: 'Dedicated support team for your account' },
  { icon: CheckCircle2, title: 'Quality Assurance', description: 'Post-service inspection and sign-off' },
  { icon: Building2, title: 'Property Profiles', description: 'Complete digital records of your property' }
];

const STEP_ICONS = [Sparkles, Lightbulb, Users, CheckCircle2];

const HomePage = () => {
  const [stats, setStats] = useState(FALLBACK_STATS);
  const [services, setServices] = useState<any[]>(FALLBACK_SERVICES);
  const [testimonials, setTestimonials] = useState(FALLBACK_TESTIMONIALS);
  const [features, setFeatures] = useState(FALLBACK_FEATURES);

  // Fetch page sections from public API
  useEffect(() => {
    const fetchPageSections = async () => {
      try {
        const response = await apiService.get('/public/page-sections/home');
        if (response.success && Array.isArray(response.data)) {
          const sections = response.data;

          const statsSection = sections.find((s: any) => s.section_key === 'stats');
          if (statsSection?.body) {
            try {
              const parsedStats = JSON.parse(statsSection.body);
              if (Array.isArray(parsedStats)) setStats(parsedStats);
            } catch (e) {
              console.warn('Failed to parse stats section:', e);
            }
          }

          const servicesSection = sections.find((s: any) => s.section_key === 'featured_services');
          if (servicesSection?.body) {
            try {
              const parsedServices = JSON.parse(servicesSection.body);
              if (Array.isArray(parsedServices)) {
                const mappedServices = parsedServices.map((svc: any, idx: number) => ({
                  ...svc,
                  icon: getIconByName(svc.icon),
                  image: svc.image || FALLBACK_SERVICES[idx % FALLBACK_SERVICES.length]?.image,
                }));
                setServices(mappedServices);
              }
            } catch (e) {
              console.warn('Failed to parse services section:', e);
            }
          }

          const testimonialsSection = sections.find((s: any) => s.section_key === 'testimonials');
          if (testimonialsSection?.body) {
            try {
              const parsedTestimonials = JSON.parse(testimonialsSection.body);
              if (Array.isArray(parsedTestimonials)) setTestimonials(parsedTestimonials);
            } catch (e) {
              console.warn('Failed to parse testimonials section:', e);
            }
          }

          const featuresSection = sections.find((s: any) => s.section_key === 'features');
          if (featuresSection?.body) {
            try {
              const parsedFeatures = JSON.parse(featuresSection.body);
              if (Array.isArray(parsedFeatures)) {
                const mappedFeatures = parsedFeatures.map((feat: any) => ({
                  ...feat,
                  icon: getIconByName(feat.icon)
                }));
                setFeatures(mappedFeatures);
              }
            } catch (e) {
              console.warn('Failed to parse features section:', e);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching page sections:', error);
      }
    };
    fetchPageSections();
  }, []);

  const getIconByName = (iconName: string) => {
    const iconMap: Record<string, any> = {
      Home, Wrench, Zap, Paintbrush, Trees, Shield, Users, Clock, Award, CheckCircle2, Building2, Lightbulb, TrendingUp
    };
    return iconMap[iconName] || Home;
  };

  return (
    <>
      <Helmet>
        <title>Virewire — Trusted Property Services, Managed Properly</title>
        <meta name="description" content="Professional property management and services platform. Expert coordination across every property category." />
      </Helmet>

      <Layout>
        <div className="bg-white overflow-hidden">
          {/* ===== HERO SECTION ===== */}
          <section className="relative isolate overflow-hidden bg-gradient-to-br from-slate-950 via-teal-950 to-teal-900 text-white">
            {/* Decorative background */}
            <div className="absolute inset-0 -z-10">
              <div className="absolute -top-40 -left-40 h-[40rem] w-[40rem] rounded-full bg-teal-500/20 blur-3xl" />
              <div className="absolute -bottom-40 -right-20 h-[36rem] w-[36rem] rounded-full bg-emerald-400/15 blur-3xl" />
              <div className="absolute top-1/3 left-1/2 h-[28rem] w-[28rem] -translate-x-1/2 rounded-full bg-cyan-400/10 blur-3xl" />
            </div>
            {/* Subtle grid */}
            <div
              className="absolute inset-0 -z-10 opacity-[0.07]"
              style={{
                backgroundImage:
                  'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)',
                backgroundSize: '56px 56px',
              }}
            />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-32 lg:pt-28 lg:pb-40">
              <div className="grid lg:grid-cols-12 gap-12 items-center">
                <div className="lg:col-span-7">
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium tracking-wide text-teal-100 backdrop-blur-md mb-6">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                    </span>
                    The connected network for property care
                  </div>

                  <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold leading-[1.05] tracking-tight mb-6">
                    Trusted Property Services,
                    <span className="block bg-gradient-to-r from-emerald-300 via-teal-200 to-cyan-200 bg-clip-text text-transparent">
                      Managed Properly.
                    </span>
                  </h1>

                  <p className="text-lg sm:text-xl text-teal-100/90 mb-10 max-w-2xl leading-relaxed">
                    We don't just list services—we orchestrate them. Experience the confidence of a fully managed property platform built for outcomes, not transactions.
                  </p>

                  <div className="flex flex-col sm:flex-row gap-4 mb-12">
                    <Link to="/service">
                      <Button className="group bg-white text-teal-800 hover:bg-teal-50 font-semibold px-8 h-12 text-base shadow-xl shadow-teal-950/40 transition-all hover:shadow-2xl hover:shadow-teal-950/50 hover:-translate-y-0.5">
                        Get Started Now
                        <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                      </Button>
                    </Link>
                    <Link to="/how-it-works">
                      <Button
                        variant="outline"
                        className="border-white/30 bg-white/5 text-white hover:bg-white/15 hover:border-white/50 font-semibold px-8 h-12 text-base backdrop-blur-md"
                      >
                        <PlayCircle className="w-4 h-4 mr-1" />
                        How It Works
                      </Button>
                    </Link>
                  </div>

                  {/* Trust Badges */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl">
                    {[
                      { label: 'Verified Pros', icon: Shield },
                      { label: 'Insured', icon: CheckCircle2 },
                      { label: 'Same-Day', icon: Clock },
                      { label: '24/7 Support', icon: PhoneCall },
                    ].map(({ label, icon: Icon }) => (
                      <div
                        key={label}
                        className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 backdrop-blur-md transition-colors hover:bg-white/10"
                      >
                        <Icon className="h-4 w-4 text-emerald-300 flex-shrink-0" />
                        <p className="text-xs font-medium text-teal-50">{label}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 3D Hero composition with real images */}
                <div className="lg:col-span-5 relative h-[32rem] hidden lg:block" style={{ perspective: '1500px' }}>
                  {/* Background card - tilted */}
                  <div
                    className="absolute top-8 right-0 w-72 h-96 rounded-3xl overflow-hidden shadow-2xl shadow-teal-950/60 ring-1 ring-white/10"
                    style={{ transform: 'rotateY(-15deg) rotateX(8deg) translateZ(-50px)' }}
                  >
                    <img
                      src={IMG.heroHouse}
                      alt="Modern home interior"
                      className="w-full h-full object-cover"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-teal-950/60" />
                  </div>

                  {/* Mid card */}
                  <div
                    className="absolute top-20 right-12 w-72 h-96 rounded-3xl overflow-hidden shadow-2xl shadow-teal-950/60 ring-1 ring-white/15"
                    style={{ transform: 'rotateY(-8deg) rotateX(4deg) translateZ(0)' }}
                  >
                    <img
                      src={IMG.modernHome}
                      alt="Beautiful home exterior"
                      className="w-full h-full object-cover"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-teal-950/70 via-transparent to-transparent" />
                  </div>

                  {/* Front card */}
                  <div
                    className="absolute top-32 right-24 w-72 h-96 rounded-3xl overflow-hidden shadow-2xl shadow-teal-950/70 ring-1 ring-white/20"
                    style={{ transform: 'rotateY(-2deg) translateZ(50px)' }}
                  >
                    <img
                      src={IMG.heroInterior}
                      alt="Luxury living room"
                      className="w-full h-full object-cover"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-teal-950/90 via-teal-950/30 to-transparent" />
                    {/* Inner content overlay */}
                    <div className="absolute inset-x-0 bottom-0 p-5">
                      <div className="rounded-xl bg-white/10 backdrop-blur-md border border-white/20 p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="flex">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} className="h-3 w-3 fill-amber-300 text-amber-300" />
                            ))}
                          </div>
                          <span className="text-white text-xs font-bold">4.9</span>
                        </div>
                        <p className="text-white text-xs">Trusted by 5,000+ property owners</p>
                      </div>
                    </div>
                  </div>

                  {/* Floating stat badge */}
                  <div
                    className="absolute -left-6 top-24 rounded-2xl bg-white/10 backdrop-blur-xl border border-white/20 p-4 shadow-2xl shadow-teal-950/50"
                    style={{ transform: 'rotateY(15deg) translateZ(80px)' }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                        <CheckCircle2 className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <p className="text-white font-bold">98%</p>
                        <p className="text-teal-100 text-xs">Satisfaction</p>
                      </div>
                    </div>
                  </div>

                  <div
                    className="absolute -left-2 bottom-12 rounded-2xl bg-white/10 backdrop-blur-xl border border-white/20 p-4 shadow-2xl shadow-teal-950/50"
                    style={{ transform: 'rotateY(15deg) translateZ(60px)' }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-cyan-400 to-teal-500 flex items-center justify-center shadow-lg shadow-cyan-500/30">
                        <Clock className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <p className="text-white font-bold">&lt; 24h</p>
                        <p className="text-teal-100 text-xs">Response time</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom curve */}
            <svg
              className="absolute bottom-0 left-0 right-0 h-16 w-full text-white"
              viewBox="0 0 1440 80"
              preserveAspectRatio="none"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M0,32 C240,80 480,80 720,48 C960,16 1200,16 1440,40 L1440,80 L0,80 Z" />
            </svg>
          </section>

          {/* ===== STATS SECTION ===== */}
          <section className="relative -mt-12 px-4 sm:px-6 lg:px-8 z-10">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6 rounded-2xl border border-gray-200 bg-white/90 backdrop-blur-md p-6 sm:p-8 shadow-[0_20px_60px_-20px_rgba(13,148,136,0.25)]">
                {stats.map((stat, idx) => (
                  <div key={idx} className="text-center">
                    <p className="text-3xl sm:text-4xl lg:text-5xl font-bold bg-gradient-to-br from-teal-700 to-emerald-600 bg-clip-text text-transparent">
                      {stat.value}
                    </p>
                    <p className="text-gray-600 text-xs sm:text-sm mt-2 uppercase tracking-wide font-medium">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ===== SIMPLE MANAGED ORCHESTRATION ===== */}
          <section className="py-24 px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <p className="text-teal-600 font-semibold uppercase tracking-widest text-xs mb-3">The Process</p>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
                  Simple, Managed Orchestration
                </h2>
                <p className="text-gray-600 max-w-2xl mx-auto text-lg">
                  We handle the complexity so you don't have to. Four steps to property peace of mind.
                </p>
              </div>

              <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                <div className="absolute left-0 right-0 top-10 hidden lg:block">
                  <div className="mx-24 h-px bg-gradient-to-r from-transparent via-teal-200 to-transparent" />
                </div>

                {[
                  { num: '1', title: 'Tell Us', desc: 'Describe your need with as much detail as you have' },
                  { num: '2', title: 'Property Quirks', desc: 'We match requirements to the right professionals' },
                  { num: '3', title: 'Assign Team', desc: 'We curate the very best vetted experts for the job' },
                  { num: '4', title: 'Results Delivered', desc: 'You get quality work, on schedule, fully documented' },
                ].map((step, idx) => {
                  const Icon = STEP_ICONS[idx];
                  return (
                    <div key={step.num} className="relative text-center group">
                      <div className="relative mx-auto mb-5 h-20 w-20" style={{ perspective: '600px' }}>
                        {/* 3D layered icon */}
                        <div
                          className="absolute inset-0 rounded-2xl bg-gradient-to-br from-teal-600 to-emerald-600 opacity-30 blur-xl transition-all group-hover:opacity-60 group-hover:blur-2xl"
                          style={{ transform: 'translateZ(-30px)' }}
                        />
                        <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-500 shadow-[0_10px_30px_-10px_rgba(13,148,136,0.5)] transition-all group-hover:scale-105 group-hover:shadow-[0_15px_40px_-10px_rgba(13,148,136,0.6)] group-hover:rotate-3" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Icon className="h-8 w-8 text-white" />
                        </div>
                        <span className="absolute -top-2 -right-2 flex h-7 w-7 items-center justify-center rounded-full bg-white text-xs font-bold text-teal-700 ring-2 ring-teal-100 shadow-md">
                          {step.num}
                        </span>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed">{step.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* ===== PROFESSIONAL SERVICES ===== */}
          <section className="relative bg-gradient-to-b from-gray-50 to-white py-24 px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-14">
                <div className="max-w-2xl">
                  <p className="text-teal-600 font-semibold uppercase tracking-widest text-xs mb-3">Our Expertise</p>
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 tracking-tight">
                    Professional Services Across Every Category
                  </h2>
                  <p className="text-gray-600 text-lg">
                    Decades of property expertise, all in one platform. Here's our current portfolio.
                  </p>
                </div>
                <Link
                  to="/services"
                  className="group inline-flex items-center gap-2 rounded-full border border-teal-200 bg-white px-5 py-2.5 text-sm font-semibold text-teal-700 hover:bg-teal-50 hover:border-teal-300 transition-colors shadow-sm"
                >
                  View all services
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {services.map((service, idx) => {
                  const IconComponent = service.icon || Home;
                  const fallback = FALLBACK_SERVICES[idx % FALLBACK_SERVICES.length];
                  const image = service.image || fallback.image;
                  return (
                    <Link
                      to="/services"
                      key={idx}
                      className="group relative overflow-hidden rounded-2xl bg-white border border-gray-200 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_25px_50px_-12px_rgba(13,148,136,0.25)] hover:border-teal-200"
                    >
                      {/* Image header */}
                      <div className="relative h-44 overflow-hidden">
                        <img
                          src={image}
                          alt={service.title}
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0'; }}
                        />
                        {/* Gradient fallback layer (always visible behind image) */}
                        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-teal-500 via-teal-600 to-emerald-700" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

                        {/* Floating icon */}
                        <div className="absolute bottom-4 left-4 flex h-12 w-12 items-center justify-center rounded-xl bg-white/95 backdrop-blur-md ring-1 ring-white/50 shadow-xl">
                          <IconComponent className="w-6 h-6 text-teal-700" />
                        </div>
                      </div>

                      <div className="p-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-700 transition-colors">{service.title}</h3>
                        <p className="text-gray-600 text-sm leading-relaxed mb-5">{service.description}</p>
                        <span className="inline-flex items-center gap-1 text-sm font-semibold text-teal-700 group-hover:text-teal-800">
                          Request Now
                          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>

          {/* ===== SOLUTIONS FOR EVERY STAKEHOLDER ===== */}
          <section className="py-24 px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="mb-14 max-w-2xl">
                <p className="text-teal-600 font-semibold uppercase tracking-widest text-xs mb-3">Built for Everyone</p>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 tracking-tight">
                  Solutions for Every Stakeholder
                </h2>
                <p className="text-gray-600 text-lg">
                  Whether you're a property owner, manager, or facilities team, we have tailored solutions.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                {/* 3D image stack */}
                <div className="relative h-[28rem]" style={{ perspective: '1200px' }}>
                  {/* Back card */}
                  <div
                    className="absolute top-0 left-12 w-72 h-80 rounded-3xl overflow-hidden shadow-2xl shadow-teal-900/20 ring-1 ring-gray-200"
                    style={{ transform: 'rotateY(8deg) rotateZ(-3deg) translateZ(-30px)' }}
                  >
                    <img
                      src={IMG.apartmentBuilding}
                      alt="Modern apartment building"
                      className="w-full h-full object-cover"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                    <div className="absolute inset-0 -z-10 bg-gradient-to-br from-teal-700 to-emerald-800" />
                  </div>

                  {/* Front card */}
                  <div
                    className="absolute top-12 right-0 w-80 h-96 rounded-3xl overflow-hidden shadow-2xl shadow-teal-900/30 ring-1 ring-gray-200"
                    style={{ transform: 'rotateY(-6deg) rotateZ(2deg) translateZ(40px)' }}
                  >
                    <img
                      src={IMG.livingRoom}
                      alt="Beautiful living room"
                      className="w-full h-full object-cover"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                    <div className="absolute inset-0 -z-10 bg-gradient-to-br from-teal-600 to-cyan-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-teal-950/50 via-transparent to-transparent" />
                  </div>

                  {/* Floating stat */}
                  <div
                    className="absolute bottom-4 left-4 rounded-2xl bg-white border border-gray-200 px-5 py-4 shadow-2xl shadow-teal-900/20 z-10"
                    style={{ transform: 'translateZ(80px)' }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center shadow-lg shadow-teal-500/30">
                        <Building2 className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-gray-900 text-lg">5,000+</p>
                        <p className="text-xs text-gray-600">Properties managed</p>
                      </div>
                    </div>
                  </div>

                  {/* Floating rating */}
                  <div
                    className="absolute top-4 right-12 rounded-2xl bg-white border border-gray-200 px-4 py-3 shadow-2xl shadow-teal-900/20 z-10"
                    style={{ transform: 'translateZ(80px)' }}
                  >
                    <div className="flex items-center gap-1 mb-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                      ))}
                    </div>
                    <p className="text-xs font-bold text-gray-900">4.9 / 5 rating</p>
                    <p className="text-[10px] text-gray-600">2,400+ reviews</p>
                  </div>
                </div>

                {/* Solutions */}
                <div className="space-y-3">
                  {[
                    { icon: Home, title: 'Property Owners', desc: 'Full lifecycle management from repairs to maintenance planning', href: '/for/owners' },
                    { icon: Building2, title: 'Landlords & Agents', desc: 'Turnkey solutions for tenant management and property upkeep', href: '/for/landlords' },
                    { icon: Users, title: 'Property Developers', desc: 'Scalable contractor networks and project coordination expertise', href: '/for/developers' },
                  ].map(({ icon: Icon, title, desc, href }) => (
                    <Link
                      key={title}
                      to={href}
                      className="group flex gap-5 rounded-2xl border border-gray-200 bg-white p-6 transition-all hover:border-teal-200 hover:shadow-[0_15px_40px_-15px_rgba(13,148,136,0.2)] hover:-translate-y-0.5"
                    >
                      <div className="flex-shrink-0">
                        <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-teal-50 to-emerald-50 ring-1 ring-teal-100 flex items-center justify-center group-hover:from-teal-100 group-hover:to-emerald-100 transition-colors">
                          <Icon className="w-6 h-6 text-teal-700" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-teal-700 transition-colors">{title}</h3>
                        <p className="text-gray-600 text-sm leading-relaxed">{desc}</p>
                        <span className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-teal-700">
                          Learn More
                          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* ===== WHY THE INDUSTRY IS MOVING TO VIREWIRE ===== */}
          <section className="relative bg-gradient-to-b from-gray-50 via-white to-gray-50 py-24 px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-14">
                <p className="text-teal-600 font-semibold uppercase tracking-widest text-xs mb-3">Why Virewire</p>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 tracking-tight max-w-3xl mx-auto">
                  Why the industry is moving to Virewire
                </h2>
                <p className="text-gray-600 max-w-2xl mx-auto text-lg">
                  We've solved the most critical problems plaguing the residential services sector.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {features.map((feature, idx) => {
                  const IconComponent = feature.icon || Home;
                  return (
                    <div
                      key={idx}
                      className="group relative overflow-hidden rounded-2xl bg-white p-6 border border-gray-200 transition-all duration-300 hover:-translate-y-1 hover:border-teal-200 hover:shadow-[0_20px_40px_-15px_rgba(13,148,136,0.2)]"
                    >
                      <div className="absolute -right-10 -top-10 h-24 w-24 rounded-full bg-teal-50 opacity-0 group-hover:opacity-100 transition-opacity" />
                      <div className="relative">
                        <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-teal-500 to-emerald-500 shadow-lg shadow-teal-500/30 group-hover:rotate-3 transition-transform">
                          <IconComponent className="w-6 h-6 text-white" />
                        </div>
                        <h3 className="font-bold text-gray-900 mb-2">{feature.title}</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">{feature.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* ===== TRUSTED BY PROPERTY LEADERS ===== */}
          <section className="py-24 px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="mb-14">
                <p className="text-teal-600 font-semibold uppercase tracking-widest text-xs mb-3">Testimonials</p>
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight max-w-2xl">
                    Trusted by Property Leaders
                  </h2>
                  <div className="flex items-center gap-3">
                    <div className="flex -space-x-2">
                      {['SM', 'JR', 'ET'].map((initials, i) => (
                        <div
                          key={i}
                          className="h-10 w-10 rounded-full bg-gradient-to-br from-teal-500 to-emerald-500 ring-2 ring-white text-white text-xs font-bold flex items-center justify-center shadow-md"
                        >
                          {initials}
                        </div>
                      ))}
                    </div>
                    <div>
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
                        ))}
                        <span className="ml-1 text-sm font-bold text-gray-900">4.9</span>
                      </div>
                      <p className="text-xs text-gray-600">from 2,400+ reviews</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {testimonials.map((testimonial, idx) => (
                  <div
                    key={idx}
                    className="group relative rounded-2xl bg-white border border-gray-200 p-7 transition-all hover:border-teal-200 hover:shadow-[0_25px_50px_-12px_rgba(13,148,136,0.2)] hover:-translate-y-1"
                  >
                    <svg
                      className="absolute top-6 right-6 h-10 w-10 text-teal-100"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M9.5,7C7,7 5,9 5,11.5V17H10.5V11.5H7.5C7.5,10.1 8.6,9 10,9V7H9.5M16.5,7C14,7 12,9 12,11.5V17H17.5V11.5H14.5C14.5,10.1 15.6,9 17,9V7H16.5Z" />
                    </svg>

                    <div className="mb-5 flex items-center gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
                      ))}
                    </div>

                    <p className="text-gray-700 mb-6 leading-relaxed relative">"{testimonial.quote}"</p>

                    <div className="flex items-center gap-3 border-t border-gray-100 pt-5">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center text-white font-bold shadow-md shadow-teal-500/20">
                        {testimonial.name?.charAt(0) || 'U'}
                      </div>
                      <div>
                        <p className="font-bold text-gray-900 text-sm">{testimonial.name}</p>
                        <p className="text-xs text-gray-600">{testimonial.role} · {testimonial.company}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ===== BENEFITS QUOTE ===== */}
          <section className="relative isolate overflow-hidden bg-gradient-to-br from-gray-950 via-slate-900 to-teal-950 text-white py-20 px-4 sm:px-6 lg:px-8">
            <div className="absolute inset-0 -z-10">
              <div className="absolute -top-32 -right-20 h-96 w-96 rounded-full bg-teal-500/20 blur-3xl" />
              <div className="absolute -bottom-32 -left-20 h-96 w-96 rounded-full bg-emerald-500/15 blur-3xl" />
            </div>
            <div
              className="absolute inset-0 -z-10 opacity-[0.05]"
              style={{
                backgroundImage:
                  'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)',
                backgroundSize: '44px 44px',
              }}
            />

            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16">
                <div>
                  <p className="text-emerald-300 font-semibold uppercase tracking-widest text-xs mb-3">The Outcome Promise</p>
                  <h3 className="text-3xl sm:text-4xl font-bold mb-5 leading-tight">
                    You're not hiring contractors—
                    <span className="block bg-gradient-to-r from-emerald-300 to-teal-200 bg-clip-text text-transparent">
                      you're buying outcomes.
                    </span>
                  </h3>
                  <p className="text-gray-300 leading-relaxed text-lg">
                    Outcomes like peace of mind. Outcomes like a functioning property. Outcomes like professionals you can trust.
                  </p>
                </div>

                <div className="space-y-4">
                  {[
                    { title: 'Scheduled, not stressed', desc: 'Services happen when you need them' },
                    { title: 'Managed, not micromanaged', desc: 'Complete coordination, zero effort' },
                    { title: 'Protected, not exposed', desc: 'Insurance coverage on every job' },
                    { title: 'Recorded, not forgotten', desc: 'Full digital history of every service' },
                  ].map((item) => (
                    <div
                      key={item.title}
                      className="flex gap-4 rounded-xl border border-white/10 bg-white/5 backdrop-blur-md p-4 transition-all hover:bg-white/10 hover:border-white/20 hover:-translate-y-0.5"
                    >
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-400/30 to-teal-500/30 ring-1 ring-emerald-400/30 flex items-center justify-center">
                          <CheckCircle2 className="w-5 h-5 text-emerald-300" />
                        </div>
                      </div>
                      <div>
                        <p className="font-semibold">{item.title}</p>
                        <p className="text-sm text-gray-400">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* ===== FINAL CTA SECTION ===== */}
          <section className="px-4 sm:px-6 lg:px-8 py-24">
            <div className="relative max-w-5xl mx-auto rounded-[2rem] overflow-hidden shadow-[0_30px_80px_-20px_rgba(13,148,136,0.4)]">
              <div className="absolute inset-0 bg-gradient-to-br from-teal-600 via-teal-700 to-emerald-700" />
              <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-emerald-300/30 blur-3xl" />
              <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-cyan-300/20 blur-3xl" />
              <svg className="absolute inset-0 w-full h-full opacity-10 pointer-events-none" preserveAspectRatio="none">
                <defs>
                  <pattern id="diagonal-stripes" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse" patternTransform="rotate(-45)">
                    <line x1="0" y1="0" x2="0" y2="20" stroke="white" strokeWidth="10" />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#diagonal-stripes)" />
              </svg>

              <div className="relative z-10 px-8 sm:px-12 lg:px-16 py-20 lg:py-24 text-center text-white">
                <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-semibold tracking-wide backdrop-blur-md mb-6">
                  <Sparkles className="h-3.5 w-3.5" />
                  Ready when you are
                </div>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-5 tracking-tight">
                  Ready to Request Your First Service?
                </h2>
                <p className="text-lg sm:text-xl text-teal-50 mb-10 max-w-2xl mx-auto leading-relaxed">
                  Experience the confidence of a truly managed property service platform today.
                </p>

                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Link to="/service">
                    <Button className="group bg-white text-teal-800 hover:bg-teal-50 font-semibold px-8 h-12 text-base shadow-xl">
                      Start Quote Now
                      <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </Link>
                  <Link to="/contact">
                    <Button
                      variant="outline"
                      className="border-white/40 bg-white/5 text-white hover:bg-white/15 hover:border-white/60 font-semibold px-8 h-12 text-base backdrop-blur-md"
                    >
                      Talk to an Expert
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </div>
      </Layout>
    </>
  );
};

export default HomePage;
